import { Modal } from 'antd';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import styled from 'styled-components';
import inCommingCallIcon from './incomming.svg';
import smsIcon from './sms-icon.svg';
import calendarIcon from './calendar-icon.svg';
import lockIcon from './lock-icon.svg';
import unlockIcon from './unlock-icon.svg';
import { formatPhone, maskPhone } from 'utils/formatPhone';
import { quickBookingV2Ref } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/Modal/QuickBookingV2';
import { ICallerIdCustomer } from './type';
import { useLocation, useNavigate } from 'react-router-dom';
import storage from 'utils/sessionStorage';
import RequestAdminPWCallback, { useRequestAdminPWCallbackRef, verifyPinCode } from 'widgets/RequestAdminLocalPW/Callback';
import appointmentApis from 'features/appointment/services/apis';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { getMessageError, IResponseDataBody } from 'services/response';
import toast from 'utils/toast';
import ModalConfirm, { useModalConfirmRef } from 'components/Modal/ModalConfirm';

type Props = {};

type ICallerIdRef = {
  open: (customer: ICallerIdCustomer) => void;
};

export const callerIdRef = React.createRef<ICallerIdRef>();
const CallerIdUI = forwardRef<ICallerIdRef, Props>((props, ref) => {
  const modalConfirm = useModalConfirmRef();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const requestAdminPWCallbackRef = useRequestAdminPWCallbackRef();
  const [customer, setCustomer] = useState<ICallerIdCustomer | null>(null);
  const onClose = () => setOpen(false);
  const [isMasked, setIsMasked] = useState(true);
  const setLoadingPage = useSetLoadingPage();

  const onBook = () => {
    onClose();
    if (!customer) return;
    quickBookingV2Ref.current?.openWithCaller(customer);
    if (!location.pathname.includes('/appointments')) {
      setTimeout(() => {
        navigate(`/store/${storage.shop_id.get()}/appointments`);
      }, 10);
    }
  };

  const handleSendSMS = async () => {
    if (!customer?.phone) return;
    setLoadingPage(true);
    try {
      const res: IResponseDataBody<boolean> = await appointmentApis.aptSendSMS(customer.phone, customer.callerId);
      if (res.data.data) {
        toast.success('SMS sent successfully');
      }
    } catch (error) {
      const msg = getMessageError(error);
      toast.error(msg);
    } finally {
      setLoadingPage(false);
    }
  };

  const onSendSMS = () => {
    if (!customer?.phone) return;
    onClose();
    modalConfirm.current?.open(`Do you want to send SMS to ${maskPhone(customer.phone)}?`, handleSendSMS);
  };

  const onToggleMasked = () => {
    if (!isMasked) return setIsMasked(true);
    requestAdminPWCallbackRef.current?.open(() => {
      setIsMasked(false);
    });
  };

  useImperativeHandle(ref, () => ({
    open: (_customer: ICallerIdCustomer) => {
      setOpen(true);
      setCustomer(_customer);
      setIsMasked(true);
    },
  }));

  return (
    <>
      <Modal centered open={open} footer={null} closable={false} closeIcon={null} width={540} destroyOnClose={false} bodyStyle={{ padding: 0, margin: 0 }}>
        <Container>
          <Body>
            <img src={inCommingCallIcon} alt="inCommingCallIcon" className='icon' />
            <div className='calling-container'>
              <span className='label'>Incoming call...</span>
              <div className='cus-phone'>
                <span className='phone-label'>{customer?.name || 'Unknown name'}</span>
                <ButtonMask type='button' onClick={onToggleMasked}>
                  <span className='phone-number'>{customer?.phone ? (isMasked ? maskPhone(customer.phone) : formatPhone(customer.phone)) : '--'}</span>
                  <img src={isMasked ? lockIcon : unlockIcon} alt="lockIcon" style={{ width: 40, height: 40 }} />
                </ButtonMask>
              </div>
            </div>
          </Body>
          <Footer>
            <button onClick={onClose} type='button' className="btn"><span>CLOSE</span></button>
            <button onClick={onSendSMS} type='button' className="btn btn-sms"><img src={smsIcon} alt="smsIcon" /><span>Send SMS</span></button>
            <button onClick={onBook} type='button' className="btn btn-submit"><img src={calendarIcon} alt="calendarIcon" /><span>BOOK</span></button>
          </Footer>
        </Container>
        <RequestAdminPWCallback v2 ref={requestAdminPWCallbackRef} />
      </Modal>
      <ModalConfirm ref={modalConfirm} />
    </>
  );
});
CallerIdUI.displayName = 'CallerIdUI';
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  overflow: hidden;
  background: #F8F9FE;
`;

const ButtonMask = styled.button`
  display: flex;
  align-items: center;
`;

const Body = styled.div`
  display: flex;
  padding: 32px 24px 16px 24px;
  align-items: center;
  gap: 24px;
  align-self: stretch;
  background: #fff;
  img.icon {
    width: 124px;
    height: 124px;
    aspect-ratio: 1/1;
  }

  .calling-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 16px;
    flex: 1 0 0;
    .label {
      color: #00A140;
      font-family: Poppins;
      font-size: 24px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }

    .cus-phone {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      align-self: stretch;
      span {
        color: #1D2129;
        font-family: Poppins;
        font-size: 36px;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        &.phone-label {
          font-size: 32px;
          font-weight: 500;
        }
      }
    }
  }
`;

const Footer = styled.div`
  display: flex;
  padding: 24px;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  button.btn {
    display: flex;
    padding: 14px 8px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    flex: 1 0 0;
    border-radius: 4px;
    border: 1px solid #CCD4DC;
    background: #CCD4DC;
    box-shadow: 0 4px 8px -3px rgba(45, 35, 66, 0.30);

    &:active {
      opacity: 0.6;
    }

    span {
      color: #1D2129;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }

    &.btn-sms {
      border: 1px solid #2D6772;
      background: #fff;
      span {
        color: #2D6772;
      }
    }

    &.btn-submit {
    border: 1px solid #00A140;
      background: #00A140;
      span {
        color: #ffffff;
      }
    }
  }
`;

export { CallerIdUI };

const CallerId = {
  open: (customer: ICallerIdCustomer) => callerIdRef.current?.open(customer),
};

export default CallerId;